import * as React from "react";

import { useCurrentUser } from "../hooks/currentUser";
import { useTrainerFeatures } from "../hooks/useTrainerFeatures";

/**
 * Feature source type - determines which feature set to check
 */
type FeatureSource = "user_feature" | "trainer_feature";

interface FeatureSwitchProps {
    /** The feature flag name to check */
    flag: string;
    /** Which feature source to check - user features or trainer features */
    source: FeatureSource;
    /** Component to render when the feature flag is present */
    enabled: React.ReactNode;
    /** Component to render when the feature flag is not present (default behavior) */
    default: React.ReactNode;
    /** Additional Check */
    additionalCondition?: () => boolean;
}

function passesConditional(condition?: () => boolean) {
    if (condition) {
        return condition();
    }

    return true;
}

/**
 * FeatureSwitch - Generic component for feature flag-based conditional rendering
 *
 * This component conditionally renders one of two components based on whether
 * a feature flag is present in the user's or trainer's feature set.
 *
 * @example
 * // User feature flag
 * <FeatureSwitch
 *     flag="FLAG"
 *     source="user_feature"
 *     enabled={<NewComponent {...props} />}
 *     default={<OriginalComponent {...props} />}
 * />
 *
 * @example
 * // Trainer feature flag
 * <FeatureSwitch
 *     flag="decimal-feed"
 *     source="trainer_feature"
 *     enabled={<DecimalFeedUI {...props} />}
 *     default={<StandardFeedUI {...props} />}
 * />
 */
export default function FeatureSwitch({
    flag,
    source,
    enabled,
    default: defaultComponent,
    additionalCondition,
}: FeatureSwitchProps): React.ReactNode {
    const { features: userFeatures } = useCurrentUser();
    const trainerFeatures = useTrainerFeatures();

    const features = source === "user_feature" ? userFeatures : trainerFeatures;
    const hasFeature = features.includes(flag);

    const passesChecks = passesConditional(additionalCondition);
    const featureEnabled = passesChecks && hasFeature;

    return featureEnabled ? enabled : defaultComponent;
}
